"use client";

import { useMemo, useRef, useState } from "react";
import {
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useLocalToday,
  WeekdayToggles,
  weekdaySet,
} from "@/components/tools/study/controls";
import {
  DayTable,
  SubjectTable,
} from "@/components/tools/study/study-schedule-tables";
import {
  type SubjectRow,
  SubjectRows,
} from "@/components/tools/study/subject-rows";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { downloadText } from "@/lib/tool-kit/download";
import {
  type DayNumber,
  dayFormatter,
  isoFromDay,
  parseIsoDay,
  randomToken,
} from "@/lib/tool-kit/study/dates";
import {
  buildIcsCalendar,
  ICS_MIME,
  type IcsAllDayEvent,
  icsUidFactory,
} from "@/lib/tool-kit/study/ics";
import {
  buildStudySchedule,
  type ExamSubject,
  groupDay,
  prepareSubjects,
  type StudyDay,
  sessionsPerDay,
  subjectTotals,
} from "@/lib/tool-kit/study/study-schedule";

const MAX_SPAN_DAYS = 366;
const MAX_SUBJECTS = 12;
const SESSION_LENGTHS = ["30", "45", "60", "90"] as const;
const EXAMPLE_OFFSETS = [14, 18, 9];
const EXAMPLE_DIFFICULTY = ["3", "2", "1"] as const;

/** English strings. A translated content file overrides these through `ui`. */
export const STUDY_SCHEDULE_GENERATOR_STRINGS = {
  subjectsHeading: "Exams and subjects",
  subjectLabel: "Subject",
  subjectPlaceholder: "e.g. Biology",
  examDateLabel: "Exam date",
  difficultyLabel: "Difficulty",
  difficulty: { "1": "Easy", "2": "Medium", "3": "Hard" },
  removeSubject: "Remove subject",
  addSubject: "Add subject",
  loadExample: "Try an example",
  exampleSubjects: ["Biology", "History", "Statistics"],
  startLabel: "Start studying on",
  hoursLabel: "Study hours per day",
  sessionLabel: "Session length",
  sessionOption: "{minutes} minutes",
  studyDaysLabel: "Days you study",
  weekdaysShort: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  dayOffLabel: "Add a day off (optional)",
  addDayOff: "Add day off",
  removeDayOff: "Remove day off {date}",
  results: "Your Study Schedule",
  printHeading: "Study schedule",
  perDaySummary: "{sessions} × {minutes}-minute sessions on each study day",
  studyDays: "Study days",
  totalSessions: "Sessions",
  totalHours: "Hours of study",
  bySubject: "By subject",
  colSubject: "Subject",
  colExam: "Exam",
  colDifficulty: "Difficulty",
  colStudy: "Study sessions",
  colReview: "Reviews",
  colHours: "Hours",
  dayByDay: "Day by day",
  colDate: "Date",
  colPlan: "Plan",
  colTime: "Time",
  examBadge: "Exam: {subject}",
  sessionCount: "one:{count} session|other:{count} sessions",
  review: "review",
  dayOff: "Day off",
  hoursValue: "{hours} h",
  downloadIcs: "Add to calendar (.ics)",
  print: "Print schedule",
  icsFileName: "study-schedule.ics",
  icsCalendarName: "Study schedule",
  icsStudyTitle: "Study: {list}",
  icsStudyItem: "one:{subject}|other:{subject} ×{count}",
  icsReviewItem: "{subject} (review)",
  icsExamTitle: "Exam: {subject}",
  icsDescription: "{sessions} × {minutes}-minute sessions",
  enterSubjects:
    "Add at least one subject with a name and an exam date after your start date.",
  examTooEarly:
    "{subject}: the exam is on or before your start date, so it's left out.",
  tooLong:
    "Your last exam is more than a year away. Plans run for up to {max} days.",
  enterHours: "Enter how many hours a day you can study.",
  noStudyDays: "Choose at least one day of the week to study.",
} as const;

interface Prepared {
  subjects: ExamSubject[];
  warnings: string[];
  error: string | null;
}

const EMPTY_ROWS: SubjectRow[] = [
  { id: "s1", name: "", exam: "", difficulty: "2" },
  { id: "s2", name: "", exam: "", difficulty: "2" },
];

export default function StudyScheduleGeneratorWidget() {
  const strings = useToolUi(STUDY_SCHEDULE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [rows, setRows] = useState<SubjectRow[]>(EMPTY_ROWS);
  const [start, setStart] = useState("");
  const [hours, setHours] = useState("3");
  const [minutes, setMinutes] =
    useState<(typeof SESSION_LENGTHS)[number]>("60");
  const [studyDays, setStudyDays] = useState<boolean[]>([
    true,
    true,
    true,
    true,
    true,
    true,
    false,
  ]);
  const [dayOffInput, setDayOffInput] = useState("");
  const [daysOff, setDaysOff] = useState<DayNumber[]>([]);
  const nextId = useRef(3);

  const today = useLocalToday((value) => setStart(isoFromDay(value)));

  const startDay = parseIsoDay(start);
  const prepared = useMemo((): Prepared => {
    if (startDay === null) {
      return { subjects: [], warnings: [], error: null };
    }
    const result = prepareSubjects(rows, startDay, MAX_SPAN_DAYS);
    return {
      subjects: result.subjects,
      warnings: result.tooEarly.map((subject) =>
        t(strings.examTooEarly, { subject })
      ),
      error: result.tooLong ? t(strings.tooLong, { max: MAX_SPAN_DAYS }) : null,
    };
  }, [rows, startDay, strings]);
  const perDay = Math.min(
    sessionsPerDay(parseNumberInput(hours) ?? 0, Number(minutes)),
    24
  );
  const weekdays = useMemo(() => weekdaySet(studyDays), [studyDays]);
  const offDays = useMemo(() => new Set(daysOff), [daysOff]);
  const calendar = useMemo(
    () =>
      startDay === null || perDay === 0
        ? []
        : buildStudySchedule({
            start: startDay,
            subjects: prepared.subjects,
            sessionsPerDay: perDay,
            weekdays,
            daysOff: offDays,
          }),
    [startDay, prepared.subjects, perDay, weekdays, offDays]
  );
  const totals = subjectTotals(calendar, prepared.subjects.length);
  const formatDay = useMemo(
    () =>
      dayFormatter(locale, {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
    [locale]
  );
  const hoursFor = (sessions: number) =>
    t(strings.hoursValue, {
      hours: fmt((sessions * Number(minutes)) / 60, {
        maximumFractionDigits: 1,
      }),
    });

  function updateRow(id: string, patch: Partial<SubjectRow>) {
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }

  function addRow() {
    const id = `s${nextId.current}`;
    nextId.current += 1;
    setRows((current) => [
      ...current,
      { id, name: "", exam: "", difficulty: "2" },
    ]);
  }

  function loadExample() {
    const base = startDay ?? today;
    if (base === null) {
      return;
    }
    setRows(
      strings.exampleSubjects.map((name, index) => ({
        id: `e${index}`,
        name,
        exam: isoFromDay(base + (EXAMPLE_OFFSETS[index] ?? 14)),
        difficulty: EXAMPLE_DIFFICULTY[index] ?? "2",
      }))
    );
  }

  function addDayOff() {
    const day = parseIsoDay(dayOffInput);
    if (day !== null && !daysOff.includes(day)) {
      setDaysOff([...daysOff, day].sort((a, b) => a - b));
    }
    setDayOffInput("");
  }

  function groupText(entry: StudyDay): string {
    return groupDay(entry)
      .flatMap((group) => {
        const subject = prepared.subjects[group.subject].name;
        const parts: string[] = [];
        if (group.review > 0) {
          parts.push(t(strings.icsReviewItem, { subject }));
        }
        if (group.study > 0) {
          parts.push(
            tPlural(strings.icsStudyItem, locale, group.study, {
              subject,
              count: fmt(group.study),
            })
          );
        }
        return parts;
      })
      .join(", ");
  }

  function handleIcs() {
    const uid = icsUidFactory(randomToken());
    const events: IcsAllDayEvent[] = [];
    for (const entry of calendar) {
      for (const exam of entry.exams) {
        events.push({
          uid: uid(events.length),
          start: entry.day,
          summary: t(strings.icsExamTitle, {
            subject: prepared.subjects[exam].name,
          }),
        });
      }
      if (entry.sessions.length > 0) {
        events.push({
          uid: uid(events.length),
          start: entry.day,
          summary: t(strings.icsStudyTitle, { list: groupText(entry) }),
          description: t(strings.icsDescription, {
            sessions: fmt(entry.sessions.length),
            minutes,
          }),
        });
      }
    }
    downloadText(
      strings.icsFileName,
      buildIcsCalendar({
        prodId: "-//CitationEasy//Study Schedule Generator//EN",
        name: strings.icsCalendarName,
        stamp: new Date(),
        events,
      }),
      ICS_MIME
    );
  }

  const sessionTotal = totals.reduce(
    (sum, row) => sum + row.study + row.review,
    0
  );
  const studyDayCount = calendar.filter(
    (entry) => entry.sessions.length > 0
  ).length;
  let notice: string | null = null;
  if (perDay === 0) {
    notice = strings.enterHours;
  } else if (weekdays.size === 0) {
    notice = strings.noStudyDays;
  } else if (calendar.length === 0) {
    notice = strings.enterSubjects;
  }

  return (
    <>
      <section className="space-y-4 print:hidden">
        <h2 className={WIDGET_H3}>{strings.subjectsHeading}</h2>
        <SubjectRows
          labels={strings}
          onChange={updateRow}
          onRemove={(id) =>
            setRows((current) => current.filter((item) => item.id !== id))
          }
          rows={rows}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            disabled={rows.length >= MAX_SUBJECTS}
            onClick={addRow}
            type="button"
          >
            {strings.addSubject}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.loadExample}
          </button>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-3 print:hidden">
        <TextField
          id="ss-start"
          label={strings.startLabel}
          onChange={setStart}
          type="date"
          value={start}
        />
        <TextField
          id="ss-hours"
          label={strings.hoursLabel}
          numeric
          onChange={setHours}
          value={hours}
        />
        <SelectField
          id="ss-session"
          label={strings.sessionLabel}
          onChange={setMinutes}
          options={SESSION_LENGTHS.map((value) => ({
            value,
            label: t(strings.sessionOption, { minutes: value }),
          }))}
          value={minutes}
        />
        <div className="sm:col-span-3">
          <WeekdayToggles
            labels={strings.weekdaysShort}
            legend={strings.studyDaysLabel}
            name="ss-day"
            onChange={setStudyDays}
            value={studyDays}
          />
        </div>
        <div className="space-y-2 sm:col-span-3">
          <label className={LABEL} htmlFor="ss-dayoff">
            {strings.dayOffLabel}
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <input
              className="rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="ss-dayoff"
              onChange={(event) => setDayOffInput(event.target.value)}
              type="date"
              value={dayOffInput}
            />
            <button
              className={BUTTON_SECONDARY}
              disabled={parseIsoDay(dayOffInput) === null}
              onClick={addDayOff}
              type="button"
            >
              {strings.addDayOff}
            </button>
            {daysOff.map((day) => (
              <button
                aria-label={t(strings.removeDayOff, { date: formatDay(day) })}
                className={BUTTON_SMALL}
                key={day}
                onClick={() =>
                  setDaysOff(daysOff.filter((item) => item !== day))
                }
                type="button"
              >
                {formatDay(day)} ×
              </button>
            ))}
          </div>
        </div>
      </section>

      <section aria-live="polite" className="space-y-5">
        {prepared.error ? <ErrorNotice>{prepared.error}</ErrorNotice> : null}
        {prepared.warnings.map((warning) => (
          <Notice key={warning}>{warning}</Notice>
        ))}
        {notice && !prepared.error ? <Notice>{notice}</Notice> : null}
        {calendar.length > 0 && !notice ? (
          <>
            <h2 className={WIDGET_H2}>
              <span className="print:hidden">{strings.results}</span>
              <span className="hidden print:inline">
                {strings.printHeading}
              </span>
            </h2>
            <p className="text-muted-foreground text-sm">
              {t(strings.perDaySummary, { sessions: fmt(perDay), minutes })}
            </p>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.studyDays}
                value={fmt(studyDayCount)}
              />
              <StatCard
                label={strings.totalSessions}
                value={fmt(sessionTotal)}
              />
              <StatCard
                label={strings.totalHours}
                value={hoursFor(sessionTotal)}
              />
            </StatGrid>
            <h3 className={WIDGET_H3}>{strings.bySubject}</h3>
            <SubjectTable
              difficulty={strings.difficulty}
              fmt={fmt}
              formatDay={formatDay}
              head={[
                strings.colSubject,
                strings.colExam,
                strings.colDifficulty,
                strings.colStudy,
                strings.colReview,
                strings.colHours,
              ]}
              hoursFor={hoursFor}
              subjects={prepared.subjects}
              totals={totals}
            />
            <h3 className={WIDGET_H3}>{strings.dayByDay}</h3>
            <DayTable
              calendar={calendar}
              fmt={fmt}
              formatDay={formatDay}
              hoursFor={hoursFor}
              labels={{
                head: [strings.colDate, strings.colPlan, strings.colTime],
                examBadge: strings.examBadge,
                sessionCount: strings.sessionCount,
                review: strings.review,
                dayOff: strings.dayOff,
              }}
              locale={locale}
              subjects={prepared.subjects}
            />
            <div className="flex flex-wrap gap-3 print:hidden">
              <button
                className={BUTTON_PRIMARY}
                onClick={handleIcs}
                type="button"
              >
                {strings.downloadIcs}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={() => window.print()}
                type="button"
              >
                {strings.print}
              </button>
            </div>
          </>
        ) : null}
      </section>
    </>
  );
}
